import styled, { css } from 'styled-components';
import { up } from '@/themes';
import { gradientTextStyle } from './typography';

const Grid = styled.div`
  /* The 1px gap lets the border colour show through as hairlines. */
  background-color: var(--border);
  border-radius: var(--radius-3xl);
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-md);
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow: hidden;

  ${up.lg} {
    grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  }
`;

const Cell = styled.div`
  background-color: var(--card);

  /* An odd count would leave an empty cell on the two-column grid. */
  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: span 2;

      ${up.lg} {
        grid-column: span 1;
      }
    `};
`;

const Stat = styled.dl`
  align-items: center;
  display: flex;
  flex-direction: column-reverse;
  gap: 4px;
  height: 100%;
  margin: 0;
  padding: 24px 16px;
  text-align: center;

  ${up.sm} {
    padding-bottom: 32px;
    padding-top: 32px;
  }
`;

const Label = styled.dt`
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  line-height: 16px;
  text-transform: uppercase;
`;

const Value = styled.dd`
  ${gradientTextStyle};
  font-size: 36px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 40px;
  margin: 0;

  ${up.sm} {
    font-size: 48px;
    line-height: 1;
  }
`;

/**
 * Band of headline numbers divided by hairlines, values in the brand gradient.
 * Two columns on small screens, one row from 1024px up.
 *
 *   <StatsRow
 *     stats={[
 *       { value: siteStats.labels(locale).brandCount, label: 'Brands' },
 *       { value: siteStats.labels(locale).countryCount, label: 'Countries' },
 *     ]}
 *   />
 *
 * Values are display strings. Take marketing numbers from data/site-stats.js.
 */
const StatsRow = ({ className, stats = [] }) => {
  const odd = stats.length % 2 === 1;

  return (
    <Grid className={className} $columns={Math.min(Math.max(stats.length, 1), 4)}>
      {stats.map((stat, index) => (
        <Cell key={stat.label} $wide={odd && index === stats.length - 1}>
          <Stat>
            <Label>{stat.label}</Label>
            <Value>{stat.value}</Value>
          </Stat>
        </Cell>
      ))}
    </Grid>
  );
};

export default StatsRow;
